import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import Head from 'components/head'

import getConfig from 'next/config'
const {
  publicRuntimeConfig: { defaultDescription, defaultAuthorBanner },
} = getConfig()

import { withDispatch } from 'store'
import { useAuthor, getAuthorBySlug } from 'store/author'
import { getContent } from 'store/article'
import ContentLayout from 'components/layout/content'
import AuthorListing from 'components/layout/listing/author'
import Container, { Col } from 'components/container'

import { toInt } from 'util/conversion'
import { getImage } from 'util/image'

import { event, set } from 'util/analytics'

const AuthorHelmet = (author) => {
  const image = getImage([author.image], defaultAuthorBanner)
  return (
    <Head>
      <title>{author.name}</title>
      <meta name="description" content={author.description || author.name || defaultDescription} />
      <meta property="og:title" content={author.name} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        property="og:description"
        content={author.description || author.name || defaultDescription}
      />
      <meta property="og:image" content={image} />
    </Head>
  )
}

const AuthorIndex = ({ id }) => {
  const author = useAuthor(id)

  useEffect(() => {
    set({
      author: author.name,
    })
    event({
      category: 'author',
      action: 'list',
      label: `/author/${author.slug}`,
    })
  }, [author])

  if (author.landingPage) {
    return (
      <>
        {AuthorHelmet(author)}
        <Container>
          <Col>
            <ContentLayout id={author.landingPage} authorId={author.id} />
          </Col>
        </Container>
      </>
    )
  }
  return (
    <>
      {AuthorHelmet(author)}
      <Container>
        <AuthorListing id={author.id} />
      </Container>
    </>
  )
}
AuthorIndex.propTypes = {
  id: PropTypes.string.isRequired,
}
AuthorIndex.delayInitialProps = true
AuthorIndex.getInitialProps = async (context) => {
  const {
    query,
    store,
    fetchArticleContent,
    fetchMissing,
    fetchAuthorsBySlug,
    setAuthorHero,
    setArticleHero,
  } = context

  let author = getAuthorBySlug(store, query.author)
  if (!author) {
    const [found, ...rest] = await fetchAuthorsBySlug([query.author]) // eslint-disable-line no-unused-vars
    author = found
    if (!author) {
      return {
        error: 404,
      }
    }
  }

  let queries = [AuthorListing.getInitialProps({ store, id: author.id, page: query.page })]
  if (author.landingPage) {
    const content = getContent(store, author.landingPage)
    if (!content) {
      const getLandingPage = async (id) => {
        const [res, item] = await fetchArticleContent(id) // eslint-disable-line no-unused-vars
        await fetchMissing([item])
      }
      queries.push(getLandingPage(author.landingPage))
    }
  }
  await Promise.all(queries)
  if (author.landingPage) {
    setArticleHero(author.landingPage, { subHeading: author.name })
  } else {
    setAuthorHero(author.id)
  }

  return { query: { page: toInt(query.page, 1), author: query.author }, id: author.id }
}
export default withDispatch({
  author: ['fetchAuthorsBySlug'],
  article: ['fetchArticleContent'],
  shared: ['fetchMissing'],
  hero: ['setAuthorHero', 'setArticleHero'],
})(AuthorIndex)
